// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AgentSteps.test.tsx
import {
  type AskPunttAgentStep,
  type AskPunttCheckProgress,
} from "@mg/schemas/src/christo/askPuntt";
import {
  CaretDown,
  CaretRight,
  CheckCircle,
  CircleNotch,
  WarningCircle,
  XCircle,
} from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";
import { useState } from "react";

import {
  STATUS_DONE_ICON_CLASS,
  STATUS_PANEL_ITEMS_MUTED_CLASS,
  STATUS_PANEL_TOGGLE_HOVER_CLASS,
  STATUS_PANEL_TOGGLE_MUTED_CLASS,
} from "./statusPanelTokens";
import {
  agentDisplayName,
  type SubagentCardEntry,
  SubagentCardGrid,
  SubagentCardGroup,
  subagentReportFailed,
  type SubagentReportView,
  useReadableSubagentText,
} from "./SubagentCardGrid";
// why: the salvage parser lives in a leaf module now (review #5399) -- it is
// pure string/JSON logic, this file was already past the 400-line ceiling, and
// the cut-point cases are only falsifiable when they can be called directly.
import {
  capabilityInstructions,
  fanOutReports,
  parseTruncatedJson,
  stringField,
} from "./truncatedJson";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";

type StepPair = {
  call: AskPunttAgentStep & { type: "tool_call" };
  result?: AskPunttAgentStep & { type: "tool_result" };
};

type SubagentRequest = {
  name: string;
  task: string;
  /**
   * Slot in the RAW `args.requests` array, kept because warhol's
   * `run_subagents` returns "one report per request, in order" over that raw
   * list (`zip(requests, outcomes, strict=True)` in `agents/spawn.py`) -- even
   * for a request this panel drops, which still gets a `failed` report in its
   * slot from `_lookup`. Matching reports by position AFTER the drop shifted
   * every later report up one row, so a surviving subagent showed its
   * neighbour's verdict.
   */
  sourceIndex: number;
};

/**
 * The verdicts christo lifts out of a `run_subagents` return BEFORE capping
 * `summary`. One name, so the rows and the header read the same wire field.
 */
type SubagentOutcomes = Extract<
  AskPunttAgentStep,
  { type: "tool_result" }
>["subagentOutcomes"];

function parseSubagentRequests(
  args: Record<string, unknown> | undefined,
): SubagentRequest[] {
  const requests = args?.requests;
  if (!Array.isArray(requests)) return [];
  return requests
    .map((entry, sourceIndex) => {
      if (entry == null || typeof entry !== "object") return null;
      const row = entry as Record<string, unknown>;
      const name = stringField(row.name);
      const task = stringField(row.task);
      if (!name) return null;
      return { name, task, sourceIndex };
    })
    .filter((row): row is SubagentRequest => row != null);
}

function parseSubagentReports(summary: string): SubagentReportView[] | null {
  const trimmed = summary.trim();
  // why: warhol's budget capability wraps a fan-out's list as
  // `{result: [...], budget: {...}}`, so the reports JSON starts with `{`, not
  // `[`. Requiring `[` dropped every body of a real fan-out -- the rows still
  // appeared, from the outcomes christo lifts out, but each one expanded to an
  // empty box.
  if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) return null;
  const parsed = fanOutReports(parseTruncatedJson(trimmed));
  if (!parsed) return null;
  return parsed.map((entry) =>
    entry != null && typeof entry === "object"
      ? (entry as SubagentReportView)
      : {},
  );
}

function parseSubagentReport(summary: string): SubagentReportView | null {
  const trimmed = summary.trim();
  if (!trimmed.startsWith("{")) return null;
  const parsed = parseTruncatedJson(trimmed);
  return parsed != null && typeof parsed === "object"
    ? (parsed as SubagentReportView)
    : null;
}

/**
 * The report rows for a fan-out, from both sources the step carries.
 *
 * The capped summary is the only source with report BODIES, but it is also
 * unreliable in a way that matters here: `parseSubagentReports` salvages
 * truncated JSON, so it can hand back the first few reports of a six-reviewer
 * fan-out and drop the rest -- and the dropped ones are exactly where a failure
 * hides. The outcomes christo lifts out before capping are complete and carry
 * every verdict, so they decide the ROWS, and the parsed reports only fill in
 * the bodies.
 */
function mergeSubagentReports(
  parsed: SubagentReportView[] | null,
  outcomes: SubagentOutcomes,
): SubagentReportView[] | null {
  if (!outcomes?.length) return parsed;
  return outcomes.map((outcome, index) => {
    const body = parsed?.[index];
    return {
      ...body,
      name: outcome.name || body?.name || "",
      // christo keeps a slot it could not read rather than dropping it, so the
      // index pairing above still holds; such a slot carries no verdict, and
      // the body's own status is a better answer than blanking the row.
      status: outcome.status || body?.status,
      ...(outcome.error ? { error: outcome.error } : {}),
    };
  });
}

/**
 * warhol's two failure statuses (`contracts/progress.py::ProgressStatus`).
 */
const FAILED_CHECK_STATUSES = new Set(["failed", "timed_out"]);

/** The third one: a check that did not run, with its own reason in `reason`. */
const SKIPPED_CHECK_STATUS = "skipped";

/**
 * One row's tick. `failed` and `blocked` are the two ways a row did not
 * finish, and at most one of them is ever set.
 */
export type RowTick = {
  done: boolean;
  failed?: boolean;
  /**
   * A check that did not run. warhol says `skipped`, the story's word is
   * `blocked`, and it is neither the finished tick nor the red cross: nothing
   * went wrong with the check, it never happened.
   */
  blocked?: boolean;
  /** Why it did not run, as the producer sent it. Empty when it sent none. */
  blockedReason?: string;
};

/**
 * The tick for one row, from warhol's own report of the leg behind it.
 *
 * `done = !!pair.result || !isStreaming` ticks every row of a fan-out together
 * when the whole call returns — so a reviewer that timed out was drawn exactly
 * like one that finished. warhol publishes each leg the moment IT settles,
 * which is what lets the rows be told apart while the turn runs.
 *
 * The NEWEST entry for a name decides: a leg the model retried publishes twice
 * and the second one is the truth. A name warhol has published nothing for —
 * an older session, a turn whose progress row could not be read, and every
 * turn that is not warhol's — keeps the old fallback exactly, so nothing that
 * works today changes.
 */
export function rowTick(
  checkProgress: readonly AskPunttCheckProgress[] | undefined,
  name: string,
  fallbackDone: boolean,
  rowCount = 1,
): RowTick {
  const entries = (checkProgress ?? []).filter((entry) => entry.name === name);
  // No entry: defer to the report, by leaving `failed` ABSENT rather than
  // false. A plain `false` would override `subagentReportFailed(report)` and
  // draw a reviewer that failed as a finished tick — the exact defect the
  // report's own status was added for.
  if (entries.length === 0) return { done: fallbackDone };
  const failedCount = entries.filter((entry) =>
    FAILED_CHECK_STATUSES.has(entry.status),
  ).length;
  // `skipped` is the status warhol publishes for a check that did NOT run —
  // every spec-only check on a review with no spec attached, each under its
  // own name and with its reason (`tools/artwork/detcheck/passes.py`). It is
  // not a failure and it is not a finished tick: read as `done` it drew the
  // green check beside work that never happened, which is the opposite of the
  // clause it exists for.
  const skipped = entries.filter(
    (entry) => entry.status === SKIPPED_CHECK_STATUS,
  );

  // More than one row shares this name and the legs did NOT all end the same
  // way — warhol runs one `evidence-verifier` per intent, in one call. warhol
  // publishes in FINISH order and these rows are drawn in REQUEST order, so
  // which row is which is not knowable here. Taking the newest entry for all
  // of them drew a timed-out verifier as a green tick beside its finished
  // twin; marking them all failed would libel the ones that worked. Neither
  // row can be attributed, so both keep the pair's own tick — and a skip is
  // unattributable in exactly the same way, so it takes the same answer.
  if (
    rowCount > 1 &&
    ((failedCount > 0 && failedCount < entries.length) ||
      (skipped.length > 0 && skipped.length < entries.length))
  ) {
    return { done: fallbackDone };
  }
  if (failedCount > 0) return { done: false, failed: true };
  if (skipped.length > 0) {
    // The newest skip carries the reason that still holds, by the same
    // publish-order rule the rest of this function reads: warhol APPENDS each
    // entry as it settles (`ask_puntt_stream.py`), so the last one is the
    // latest thing known about this name.
    return {
      done: false,
      blocked: true,
      blockedReason: skipped[skipped.length - 1].reason ?? "",
    };
  }
  return { done: true, failed: false };
}

function SubagentToolStep({
  stepId,
  requestIndex,
  title,
  body: rawBody,
  report,
  done,
  failedStatus,
  blockedStatus,
  blockedReason,
}: {
  stepId: string;
  requestIndex?: number;
  title: string;
  body: string;
  report?: SubagentReportView;
  done: boolean;
  /**
   * warhol says this leg did not do the work. Wins over both `done` and the
   * report: while the turn runs there is no report to read, and once one lands
   * the report's own status already agrees.
   */
  failedStatus?: boolean;
  /**
   * warhol says this check never ran. Wins over `done` and over the report for
   * the same reason `failedStatus` does, and more so: a check that did not run
   * has no report behind it, so nothing else on the row can say it.
   */
  blockedStatus?: boolean;
  /**
   * Why it never ran, in the producer's own words ("…has no text layer…"). The
   * row draws it: a blocked check whose reason is invisible is half the clause.
   */
  blockedReason?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const suffix = requestIndex != null ? `-${requestIndex}` : "";
  const rowId = `${stepId}${suffix}`;
  const { body, reportText } = useReadableSubagentText(rawBody, report);
  const failed = failedStatus ?? subagentReportFailed(report);

  return (
    <div
      data-testid={`agent-step-subagent-${rowId}`}
      className={cx(
        "overflow-hidden",
        interactiveSurfaceClasses({
          expandable: true,
          muted: true,
          compact: true,
          open: expanded,
        }),
      )}
    >
      <button
        type="button"
        data-testid={`agent-step-subagent-toggle-${rowId}`}
        aria-expanded={expanded}
        // why: the row's spoken name carries the reason too. The button's
        // `aria-label` REPLACES its contents, so a blocked row announced only
        // by its title would drop the one thing the clause asks for.
        aria-label={blockedReason ? `${title} — ${blockedReason}` : title}
        onClick={() => setExpanded((open) => !open)}
        className={`${STATUS_PANEL_TOGGLE_MUTED_CLASS} ${STATUS_PANEL_TOGGLE_HOVER_CLASS}`}
      >
        {failed ? (
          <XCircle
            size={14}
            weight="fill"
            className="text-tomato-900 shrink-0"
            aria-hidden
          />
        ) : blockedStatus ? (
          // The rail's own blocked glyph (`AskV2RightRail.tsx`): not a tick,
          // because the check did not run, and not a spinner, because nothing
          // is still happening.
          <WarningCircle
            size={14}
            weight="fill"
            data-testid={`agent-step-subagent-blocked-${rowId}`}
            className="shrink-0 text-puntt-neutral-gray-10"
            aria-hidden
          />
        ) : done ? (
          <CheckCircle
            size={14}
            weight="fill"
            className={`shrink-0 ${STATUS_DONE_ICON_CLASS}`}
            aria-hidden
          />
        ) : (
          <CircleNotch
            size={14}
            className="shrink-0 animate-spin text-puntt-accent-9 motion-reduce:animate-none"
            aria-hidden
          />
        )}
        <Text
          size="1"
          weight="regular"
          data-testid="agent-step-name"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
        >
          {title}
        </Text>
        {blockedStatus && blockedReason ? (
          <span
            data-testid={`agent-step-subagent-blocked-reason-${rowId}`}
            className="min-w-0 shrink truncate text-[12px] text-puntt-neutral-gray-10"
          >
            {blockedReason}
          </span>
        ) : null}
        {expanded ? (
          <CaretDown
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        ) : (
          <CaretRight
            size={14}
            className="shrink-0 text-puntt-neutral-gray-12"
            aria-hidden
          />
        )}
      </button>
      {expanded ? (
        <div
          data-testid={`agent-step-subagent-body-${rowId}`}
          className={STATUS_PANEL_ITEMS_MUTED_CLASS}
        >
          {body ? (
            <Text
              size="1"
              className="whitespace-pre-wrap text-puntt-neutral-gray-12"
            >
              {body}
            </Text>
          ) : null}
          {reportText ? (
            <Text
              size="1"
              className={`whitespace-pre-wrap text-puntt-neutral-gray-9 ${body ? "mt-1" : ""}`}
            >
              {reportText}
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One expandable row per subagent spawn / skill load; a fan-out is a card grid.
 *
 * `checkProgress` is what warhol has published so far this turn. It is what
 * lets one reviewer's card tick while its neighbours are still running, instead
 * of every card ticking together when the whole fan-out returns.
 */
export function SubagentToolStepsForPair({
  pair,
  isStreaming,
  checkProgress,
}: {
  pair: StepPair;
  isStreaming: boolean;
  checkProgress?: AskPunttCheckProgress[];
}) {
  const call = pair.call;
  const done = !!pair.result || !isStreaming;
  const stepId = call.stepId || call.timestamp;
  const args = call.args ?? {};

  if (call.tool === "run_subagents") {
    const requests = parseSubagentRequests(args);
    const parsedReports = pair.result
      ? parseSubagentReports(pair.result.summary)
      : null;
    const outcomes = pair.result?.subagentOutcomes;
    const reports = mergeSubagentReports(parsedReports, outcomes);
    /**
     * How many rows this fan-out draws for one roster name. A name on more
     * than one row cannot be attributed to a row from warhol's finish-ordered
     * entries; see `rowTick`.
     */
    const nameCount = (name: string) =>
      requests.length > 0
        ? requests.filter((request) => request.name === name).length
        : (reports ?? []).filter((report) => stringField(report.name) === name)
            .length;
    const ticked = (
      name: string,
      entry: Omit<
        SubagentCardEntry,
        "done" | "failed" | "blocked" | "blockedReason"
      >,
    ): SubagentCardEntry => {
      const tick = rowTick(checkProgress, name, done, nameCount(name));
      return {
        ...entry,
        done: tick.done,
        failed: tick.failed,
        blocked: tick.blocked,
        blockedReason: tick.blockedReason,
      };
    };
    if (requests.length === 0) {
      // why: rembrandt converts a `tool_call` into a `tool_result` in the SAME
      // array slot, and the `tool_result` schema carries no `args` -- so a
      // settled fan-out, and EVERY reloaded thread, reaches this panel with no
      // `requests` at all and the reports JSON alone in `summary`. The bare
      // fallback row passed no report, so each finished fan-out collapsed to
      // one nameless row whose expanded box was empty: every subagent and its
      // verdict was dropped. That is the same wire fact `run_subagent` and
      // `load_capability` already recover from below.
      if (!reports || reports.length === 0) {
        return (
          <SubagentToolStep
            stepId={stepId}
            title={call.displayName || "Run subagents"}
            body=""
            done={done}
          />
        );
      }
    }
    const entries: SubagentCardEntry[] =
      requests.length > 0
        ? requests.map((request, index) =>
            ticked(request.name, {
              rowId: `${stepId}-${index}`,
              title: agentDisplayName(request.name),
              body: request.task,
              // By the request's slot in the wire array, not its row here: a
              // dropped request still owns a report slot upstream.
              report: reports?.[request.sourceIndex],
            }),
          )
        : (reports ?? []).map((report, index) =>
            ticked(stringField(report.name), {
              rowId: `${stepId}-${index}`,
              title:
                agentDisplayName(stringField(report.name)) ||
                call.displayName ||
                "Run subagents",
              body: "",
              report,
            }),
          );
    if (entries.length === 1) {
      const [only] = entries;
      return (
        <SubagentToolStep
          stepId={stepId}
          requestIndex={0}
          title={only.title}
          body={only.body}
          report={only.report}
          done={only.done}
          failedStatus={only.failed}
          blockedStatus={only.blocked}
          blockedReason={only.blockedReason}
        />
      );
    }
    return <SubagentCardGroup gridId={stepId} entries={entries} />;
  }

  if (call.tool === "run_subagent") {
    const name =
      agentDisplayName(stringField(args.name)) ||
      call.displayName ||
      "Run subagent";
    const task = stringField(args.task);
    const report = pair.result
      ? (parseSubagentReport(pair.result.summary) ?? undefined)
      : undefined;
    const tick = rowTick(checkProgress, stringField(args.name), done);
    return (
      <SubagentToolStep
        stepId={stepId}
        title={name}
        body={task}
        report={report}
        done={tick.done}
        failedStatus={tick.failed}
        blockedStatus={tick.blocked}
        blockedReason={tick.blockedReason}
      />
    );
  }

  if (call.tool === "load_capability") {
    const instructions =
      stringField(args.instructions) ||
      capabilityInstructions(pair.result?.summary);
    return (
      <SubagentToolStep
        stepId={stepId}
        title={call.displayName || "Load skill"}
        body={instructions}
        done={done}
      />
    );
  }

  return null;
}

/** Consecutive same-name `run_subagent` calls as one card grid. */
export function SubagentRunCardGrid({
  gridId,
  pairs,
  isStreaming,
  checkProgress,
}: {
  gridId: string;
  pairs: StepPair[];
  isStreaming: boolean;
  checkProgress?: AskPunttCheckProgress[];
}) {
  const entries: SubagentCardEntry[] = pairs.map((pair) => {
    const args = pair.call.args ?? {};
    const name = stringField(args.name);
    const rowCount = pairs.filter(
      (candidate) => stringField(candidate.call.args?.name) === name,
    ).length;
    const tick = rowTick(
      checkProgress,
      name,
      !!pair.result || !isStreaming,
      rowCount,
    );
    return {
      rowId: pair.call.stepId || pair.call.timestamp,
      title: agentDisplayName(name) || pair.call.displayName || "Run subagent",
      body: stringField(args.task),
      report: pair.result
        ? (parseSubagentReport(pair.result.summary) ?? undefined)
        : undefined,
      done: tick.done,
      failed: tick.failed,
      blocked: tick.blocked,
      blockedReason: tick.blockedReason,
    };
  });
  return <SubagentCardGrid gridId={gridId} entries={entries} />;
}

export function countSubagentLogicalSteps(
  tool: string,
  args: Record<string, unknown> | undefined,
  /**
   * The merged `tool_result` summary, when one landed. It is the only place a
   * settled fan-out still lists its subagents — the in-place merge drops
   * `args.requests` — so without it the header counted a finished 3-subagent
   * fan-out as "1 Step" and disagreed with the rows rendered under it.
   */
  resultSummary?: string,
  /**
   * The verdicts christo lifted out BEFORE capping `summary`. Without them the
   * header counted only what the capped JSON still parsed into: a settled
   * fan-out whose summary was cut mid-object salvaged nothing and read
   * "1 Step" above the N reviewer rows `mergeSubagentReports` rendered from
   * these same outcomes.
   */
  outcomes?: SubagentOutcomes,
): number {
  if (tool === "run_subagents") {
    const requests = parseSubagentRequests(args);
    if (requests.length > 0) return requests.length;
    const parsed = resultSummary ? parseSubagentReports(resultSummary) : null;
    // The rows come from `mergeSubagentReports`, so the count does too --
    // anything else re-derives the row set and can disagree with it again.
    const reports = mergeSubagentReports(parsed, outcomes);
    return reports && reports.length > 0 ? reports.length : 1;
  }
  return 1;
}
